import { Map } from 'immutable';
import { noop } from 'lodash';
import { DO_NOTHING, NEW_QUIZ_ID, QUIZ_SAVE_CALL, PARENT_TYPE_BANK, PARENT_TYPE_QUIZ, PARENT_TYPE_QUIZ_SESSION } from '@instructure/quiz-common';
import Fetcher from '../../common/util/Fetcher';
import { getQTIInfo } from './qtiImports';
import { changeQuizAllowBacktracking, changeQuizAllowClearMCSelection, changeQuizChoiceElimination, changeQuizHasTimeLimit, changeQuizOneAtATimeType, changeQuizDefaultRceSettings, changeQuizShuffleQuestions, changeQuizShuffleAnswers, changeQuizRequireStudentAccessCode, changeQuizStudentAccessCode, changeSessionTimeLimitInSeconds, changeQuizFilterIpAddress, changeQuizIpFilters, changeQuizCalculatorType, changeResultsFeedback, changeResultsFeedbackEnabled, changeQuizDisableDocumentAccess, changeQuizDetectMultipleSessions } from '../../common/actions/modifications';
import { handleQuizResponse } from '../../common/api/callHandlers';
import { getUploadUrl, sendFileToS3 } from '../../common/api/fileUpload';
import { interactionTypeFetch } from '../../common/api/interactionTypes';
import { getExistingQuizAndItems, quizFetchHandler } from '../../common/api/quizzes';
import config from '../../config';
var quizApiEndpoint = '/api/quizzes';

// ==== CALLBACKS ====

var handleQuizFetch = function handleQuizFetch(dispatch, quiz) {
  quizFetchHandler(dispatch, quiz);

  // if feature is on, fetch qti import data
  dispatch(getQTIInfo(quiz.id));
};

// ==================

// ==========================================
// called by the router when a quiz is needed
// knows how to create a new quiz, fetch
// an existing quiz, or set activeId depending
// on the current state & params
// ==========================================

export function quizBuildingSession(routerParams) {
  return function (dispatch, getState) {
    var contextId = routerParams.contextId;
    var quizId = routerParams.activeQuizId;
    var createNew = routerParams.createNew;
    var needsQuiz = quizId === NEW_QUIZ_ID || !quizId;

    // create a new quiz if none exists
    if (createNew && needsQuiz) {
      return dispatch(startNewQuiz(contextId));
    }

    // do nothing if already on quiz and questions are loaded
    var currentQuizId = getState().getIn(['quizzes', 'activeQuizId']);
    var quizEntries = getState().get('quizEntries', Map());
    if (currentQuizId === quizId && quizEntries.size > 0) {
      return {
        type: DO_NOTHING
      };
    }
    dispatch(getExistingQuizAndItemsForBuilding(quizId));
  };
}

// ====================================
// save quiz changed, and on success
// add these changes to the quiz store
// ====================================

export function saveQuizChange(workingQuiz, quiz) {
  if (workingQuiz.equals(quiz)) {
    // cannot just return null since
    // dispatch expects an action
    return {
      type: DO_NOTHING
    };
  }
  var url = "".concat(quizApiEndpoint, "/").concat(workingQuiz.get('id'));
  var quizData = JSON.stringify(workingQuiz.validData().toJS());
  return function (dispatch, getState) {
    var params = {
      body: quizData
    };
    var fetcher = new Fetcher({
      callType: QUIZ_SAVE_CALL
    });
    return fetcher.patch(url, params).then(function (quizData) {
      dispatch(handleQuizResponse(quizData));
    });
  };
}

// =================================
// create a new quiz, add its data,
// and add it as the active quiz
// =================================

export function startNewQuiz(contextId) {
  return function (dispatch, getState) {
    var newQuizData = JSON.stringify({
      title: 'Add Title...',
      context_id: contextId || config.defaultContextId
    });
    var params = {
      body: newQuizData
    };
    var fetcher = new Fetcher();
    return fetcher.post(quizApiEndpoint, params).then(function (quizData) {
      handleQuizFetch(dispatch, quizData);
    }).then(function () {
      dispatch(interactionTypeFetch());
    });
  };
}

// ====================================
// fetch the data for an existing quiz
// and add it as the active quiz
// ====================================

export function getExistingQuizAndItemsForBuilding(quizId) {
  var fetchTags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  // TODO: handleQuizFetch is not handled in getExistingQuizAndItems,
  //       so determine impact of getQTIInfo not being dispatched
  return getExistingQuizAndItems(quizId, fetchTags, handleQuizFetch);
}

// =========================================
//  media upload endpoint funcs
// =========================================

function getPathToGetUrl(parentType, parentId) {
  switch (parentType) {
    case PARENT_TYPE_BANK:
      return "/api/banks/".concat(parentId, "/items/media_upload_url");
    case PARENT_TYPE_QUIZ:
      return "/api/quizzes/".concat(parentId, "/items/media_upload_url");
    case PARENT_TYPE_QUIZ_SESSION:
      return "/api/quiz_sessions/".concat(parentId, "/media_upload_url");
  }
}
export function interactionFileUpload(parentType, parentId, file, onSuccess, onError, uploadPath) {
  var withUrlCallback = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : sendFileToS3;
  return function (dispatch, getState) {
    var pathToGetUrl = uploadPath || getPathToGetUrl(parentType, parentId);
    return getUploadUrl(pathToGetUrl).then(withUrlCallback(file, onSuccess, onError));
  };
}

// ====================================
// update the data for an existing quiz
// ====================================

function setQuizProperty(quizId, properties, action) {
  var completeCb = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;
  var url = "/api/quizzes/".concat(quizId);
  var body = JSON.stringify({
    id: quizId,
    quiz: properties
  });
  return function (dispatch, getState) {
    var params = {
      body: body
    };
    var fetcher = new Fetcher({
      callType: QUIZ_SAVE_CALL
    });
    if (action !== null) {
      dispatch(action);
    }
    return fetcher.patch(url, params).then(function (response) {
      dispatch(handleQuizResponse(response));
      completeCb();
    });
  };
}
export function setAllowClearMCSelection(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    allow_clear_mc_selection: Boolean(value)
  }, changeQuizAllowClearMCSelection(quizId, value), completeCb);
}
export function setChoiceElimination(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    choice_elimination: Boolean(value)
  }, changeQuizChoiceElimination(quizId, value), completeCb);
}
export function setQuizDefaultRceSettings(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    default_rce_settings: value
  }, changeQuizDefaultRceSettings(quizId, value), completeCb);
}
export function setQuizShuffleQuestions(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    shuffle_questions: Boolean(value)
  }, changeQuizShuffleQuestions(quizId, value), completeCb);
}
export function setQuizShuffleAnswers(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    shuffle_answers: Boolean(value)
  }, changeQuizShuffleAnswers(quizId, value), completeCb);
}
export function setQuizDisableDocumentAccess(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    disable_document_access: Boolean(value)
  }, changeQuizDisableDocumentAccess(quizId, value), completeCb);
}
export function setRequireStudentAccessCode(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    require_student_access_code: Boolean(value)
  }, changeQuizRequireStudentAccessCode(quizId, value), completeCb);
}
export function setStudentAccessCodeUi(quizId, value) {
  return function (dispatch, getState) {
    dispatch(changeQuizStudentAccessCode(quizId, value));
  };
}
export function setStudentAccessCodeApi(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    student_access_code: value
  }, null, completeCb);
}
export function setOneAtATimeType(quizId, checked, completeCb) {
  var oneAtATimeType = checked ? 'question' : null;
  return setQuizProperty(quizId, {
    one_at_a_time_type: oneAtATimeType
  }, changeQuizOneAtATimeType(quizId, oneAtATimeType), completeCb);
}
export function setBacktracking(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    allow_backtracking: Boolean(value)
  }, changeQuizAllowBacktracking(quizId, value), completeCb);
}
export function setQuizHasTimeLimit(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    has_time_limit: Boolean(value)
  }, changeQuizHasTimeLimit(quizId, value), completeCb);
}
export function setSessionTimeLimitInSecondsUi(quizId, value) {
  return function (dispatch, getState) {
    dispatch(changeSessionTimeLimitInSeconds(quizId, value));
  };
}
export function setSessionTimeLimitInSecondsApi(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    session_time_limit_in_seconds: value
  }, null, completeCb);
}
export function setFilterIpAddress(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    filter_ip_address: Boolean(value)
  }, changeQuizFilterIpAddress(quizId, value), completeCb);
}
export function setIpFiltersUi(quizId, ips, namedIps) {
  return function (dispatch, getState) {
    dispatch(changeQuizIpFilters(quizId, ips, namedIps));
  };
}
export function setIpFiltersApi(quizId, ips, namedIps, completeCb) {
  return setQuizProperty(quizId, {
    filters: {
      ips: ips,
      named_ips: namedIps
    }
  }, null, completeCb);
}
export function setCalculatorType(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    calculator_type: value
  }, changeQuizCalculatorType(quizId, value), completeCb);
}
export function setResultsFeedback(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    results_feedback: value
  }, changeResultsFeedback(quizId, value), completeCb);
}
export function setResultsFeedbackEnabled(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    results_feedback_enabled: value
  }, changeResultsFeedbackEnabled(quizId, value), completeCb);
}
export function setQuizDetectMultipleSessions(quizId, value, completeCb) {
  return setQuizProperty(quizId, {
    detect_multiple_sessions: Boolean(value)
  }, changeQuizDetectMultipleSessions(quizId, value), completeCb);
}